import { BankAccount } from '@40seas/entities/bank-account';
import {
    ACHPaymentMethod,
    ACSSPaymentMethod,
    BACSPaymentMethod,
    BECSPaymentMethod,
    CardPaymentMethod,
    PaymentMethod,
    PaymentMethodProviderType,
    SEPAPaymentMethod,
    WirePaymentMethod,
} from '@40seas/entities/payment-methods';
import { PaymentMethodType } from '@40seas/models';
import { formatWalletAddress } from '@40seas/formatters';
import { getBankAccountDisplay } from '@40seas/shared/bank-account';
import camelCase from 'lodash/camelCase';
import startCase from 'lodash/startCase';

type PaymentMethodTypeDescriptionHandler = (paymentMethod: PaymentMethod) => string;

const paymentMethodTypeToDescription = new Map<PaymentMethodType, PaymentMethodTypeDescriptionHandler>([
    [PaymentMethodType.ACSS, acssAchPaymentMethodTypeDescription.bind(this)],
    [PaymentMethodType.Card, cardPaymentMethodTypeDescription.bind(this)],
    [PaymentMethodType.ACH, acssAchPaymentMethodTypeDescription.bind(this)],
    [PaymentMethodType.BACS, bacsPaymentMethodTypeDescription.bind(this)],
    [PaymentMethodType.BECS, becsPaymentMethodTypeDescription.bind(this)],
    [PaymentMethodType.SEPA, sepaPaymentMethodTypeDescription.bind(this)],
    [PaymentMethodType.Wire, wirePaymentMethodTypeDescription.bind(this)],
    [PaymentMethodType.Stablecoin, stableCoinPaymentMethodTypeDescription.bind(this)],
]);

function bacsPaymentMethodTypeDescription(paymentMethod: PaymentMethod): string {
    return (
        paymentMethod.methodTypeFields &&
        [(paymentMethod.methodTypeFields as BACSPaymentMethod).bankName, (paymentMethod.methodTypeFields as BACSPaymentMethod).last4].join(
            '...',
        )
    );
}

function becsPaymentMethodTypeDescription(paymentMethod: PaymentMethod): string {
    return (
        paymentMethod.methodTypeFields &&
        [(paymentMethod.methodTypeFields as BECSPaymentMethod).bankName, (paymentMethod.methodTypeFields as BECSPaymentMethod).last4].join(
            '...',
        )
    );
}

function acssAchPaymentMethodTypeDescription(paymentMethod: PaymentMethod): string {
    return (
        paymentMethod.methodTypeFields &&
        [
            (paymentMethod.methodTypeFields as ACHPaymentMethod | ACSSPaymentMethod).bankName,
            (paymentMethod.methodTypeFields as ACHPaymentMethod | ACSSPaymentMethod).last4,
        ].join('...')
    );
}

function cardPaymentMethodTypeDescription(paymentMethod: PaymentMethod): string {
    return (
        paymentMethod.methodTypeFields &&
        [(paymentMethod.methodTypeFields as CardPaymentMethod).brand, (paymentMethod.methodTypeFields as CardPaymentMethod).last4].join(
            '...',
        )
    );
}

function sepaPaymentMethodTypeDescription(paymentMethod: PaymentMethod): string {
    return (
        paymentMethod.methodTypeFields &&
        [(paymentMethod.methodTypeFields as SEPAPaymentMethod).bankName, (paymentMethod.methodTypeFields as SEPAPaymentMethod).last4].join(
            '...',
        )
    );
}

function wirePaymentMethodTypeDescription(paymentMethod: PaymentMethod): string {
    if (paymentMethod.providerType === PaymentMethodProviderType.Wise) {
        return getBankAccountDisplay({
            // @ts-ignore FS-4101
            bankAccountDetails: (paymentMethod.methodTypeFields as WirePaymentMethod).banksAccountDetails[0],
        } as BankAccount);
    }

    if (paymentMethod.providerType === PaymentMethodProviderType.Other) {
        return 'paid directly';
    }

    return 'Offline bank transfer';
}

export function getPaymentMethodDescription(paymentMethod: PaymentMethod): string {
    if (!paymentMethod) {
        return '';
    }

    const paymentMethodTypeToDescriptionMap = paymentMethodTypeToDescription.get(paymentMethod.methodType);
    // @ts-ignore FS-4101
    const desc = paymentMethodTypeToDescriptionMap(paymentMethod);

    return desc.charAt(0).toUpperCase() + desc.slice(1);
}

function stableCoinPaymentMethodTypeDescription(paymentMethod: PaymentMethod): string {
    return `Crypto wallet ${formatWalletAddress(paymentMethod.methodTypeFields['walletAddress'])}`;
}

export function getTransformedPaymentMethodDescription(paymentMethod: PaymentMethod) {
    if (!paymentMethod) {
        return '';
    }

    const description = getPaymentMethodDescription(paymentMethod);

    const [pmName, last4] = description.split('...');
    const stopWords = ['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'if', 'in', 'nor', 'of', 'on', 'or', 'per', 'the', 'to', 'vs'];

    const fixedCaseWords = ['NA', 'JPMorgan', 'MasterCard'];

    const transformedPaymentMethod = startCase(camelCase(pmName))
        .replace(/\b\w+\b/g, (word) => (!stopWords.includes(word.toLowerCase()) ? word : word.toLowerCase()))
        .replace(/\b\w+\b/g, (word) => fixedCaseWords.find((x) => x.toLowerCase() === word.toLowerCase()) ?? word);

    return last4 ? `${transformedPaymentMethod}...${last4}` : transformedPaymentMethod;
}
